import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { BaseApiService } from '../../../../core/base-api.service';
import { ApiResponse, PageableApiResponse } from '../../../api-response';
import { Tenant, TenantDetail, TenantRequest } from '../models/tenant.model';
import { InviteMemberRequest, ManagedRole, MemberSummary } from '../models/member.model';
import { AdminAggregates } from '../models/admin-aggregates.model';

/** Optional server-side filters for the organizations list. */
export interface TenantListFilter {
  /** Matched by Keycloak against org NAME and DOMAINS (partial). NOT alias. */
  search?: string;
  enabled?: boolean;
}

/**
 * Cross-tenant (organization) administration. Wraps the Keycloak-backed
 * `/api/v1/organizations` endpoints — list/create/update/delete require the
 * super-admin role; `getMyTenants` is available to any authenticated viewer.
 */
@Injectable({ providedIn: 'root' })
export class TenantService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly tenantsUrl = `${this.coreApiUrl}/api/v1/organizations`;
  private readonly adminUrl = `${this.coreApiUrl}/api/v1/admin`;

  /** Cross-tenant KPI totals for the overview strip. Role: super-admin. */
  getAggregates(): Observable<AdminAggregates> {
    return this.http
      .get<ApiResponse<AdminAggregates>>(`${this.adminUrl}/aggregates`)
      .pipe(map(response => response.data));
  }

  /** Organizations the current user belongs to. Role: viewer. */
  getMyTenants(): Observable<Tenant[]> {
    return this.http
      .get<ApiResponse<Tenant[]>>(`${this.tenantsUrl}/me`)
      .pipe(map(response => response.data));
  }

  /** All organizations, paginated, optionally filtered server-side. Role:
   *  super-admin. `totalElements` is exact when `enabled` is set; otherwise
   *  best-effort (Keycloak has no count endpoint). */
  getTenants(page: number, size: number, filter?: TenantListFilter): Observable<PageableApiResponse<Tenant>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (filter?.search) params = params.set('search', filter.search);
    if (filter?.enabled !== undefined) params = params.set('enabled', filter.enabled);
    return this.http.get<PageableApiResponse<Tenant>>(this.tenantsUrl, { params });
  }

  /** Enriched single organization (created/contact/region/SSO). Role: super-admin. */
  getTenant(id: string): Observable<TenantDetail> {
    return this.http
      .get<ApiResponse<TenantDetail>>(`${this.tenantsUrl}/${id}`)
      .pipe(map(response => response.data));
  }

  /** Members of an arbitrary organization, paginated. Role: super-admin.
   *  Total is best-effort while `search` is set. */
  getTenantUsers(id: string, page: number, size: number, search?: string): Observable<PageableApiResponse<MemberSummary>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (search) params = params.set('search', search);
    return this.http.get<PageableApiResponse<MemberSummary>>(`${this.tenantsUrl}/${id}/members`, { params });
  }

  /** Managed roles assignable through the invite flow. Role: super-admin. */
  getAssignableRoles(id: string): Observable<ManagedRole[]> {
    return this.http
      .get<ApiResponse<ManagedRole[]>>(`${this.tenantsUrl}/${id}/roles`)
      .pipe(map(response => response.data));
  }

  /** Invite a user into an organization. Role: super-admin. The invitee shows
   *  up in the member list with status INVITED. */
  inviteMember(id: string, body: InviteMemberRequest): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.tenantsUrl}/${id}/members`, body);
  }

  /** Remove a user's membership (does NOT delete the account). Role: super-admin. */
  removeMember(id: string, userId: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.tenantsUrl}/${id}/members/${userId}`);
  }

  /** Role: super-admin. Keycloak requires at least one domain entry. */
  createTenant(body: TenantRequest): Observable<TenantDetail> {
    return this.http
      .post<ApiResponse<TenantDetail>>(this.tenantsUrl, body)
      .pipe(map(response => response.data));
  }

  /** Role: super-admin. Response carries no `data` payload. Full-replacement
   *  body — include `attributes` (contact/region) or they get wiped. */
  updateTenant(id: string, body: TenantRequest): Observable<ApiResponse<null>> {
    return this.http.put<ApiResponse<null>>(`${this.tenantsUrl}/${id}`, body);
  }

  /** Role: super-admin. */
  deleteTenant(id: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.tenantsUrl}/${id}`);
  }
}
